import { Seo } from "../components/layout/Seo";
import { Button } from "../components/ui/Button";

type Founder = {
  id: string;
  name: string;
  roleLine: string;
  credentialLine: string;
  intro: string;
  contextLine?: string;
  photo: { src: string; alt: string };
  role: { heading: string; paragraphs: string[]; focusLabel: string; focus: string[]; focusNote?: string; pull: string };
  network?: { heading: string; intro: string; center: string; circles: string[]; note: string };
  why?: { heading: string; paragraphs: string[]; chronology: Array<[string, string, string]>; quote: string };
  background: { heading: string; intro: string; qualifications: Array<[string, string]>; practiceLabel: string; practice: string[]; mediationLabel: string; mediation: string; closing: string };
  languages: Array<[string, string]>;
  contact: { heading: string; text: string; email: string; whatsappLabel: string; whatsappUrl: string; vcard: string };
};

const polina: Founder = {
  id: "polina-zisman",
  name: "Polina Zisman",
  roleLine: "Co-Founder · Moldova–Israel Partnerships",
  credentialLine: "Attorney-at-Law · Mediator",
  intro: "Polina Zisman is a Moldova-born Israeli attorney and co-founder of Transition Bridge, bringing together legal, communication and mediation experience in the development of Moldova–Israel partnerships.",
  contextLine: "Moldova ↔ Israel · Partnerships into projects",
  photo: { src: "/assets/about/polina.jpg", alt: "Portrait of Polina Zisman, co-founder of Transition Bridge." },
  role: {
    heading: "Moldova–Israel relationships that lead to real projects",
    paragraphs: [
      "At Transition Bridge, Polina focuses on developing relationships between Moldovan businesses, institutions and experts and relevant partners in Israel.",
      "Her work includes building dialogue with businesses, public institutions, professional associations, experts and potential project partners on both sides."
    ],
    focusLabel: "Current focus",
    focus: [
      "Energy resilience",
      "Sustainable agriculture",
      "Agrivoltaics",
      "Resource efficiency",
      "Technology partnerships"
    ],
    focusNote: "Connecting Moldovan business needs with relevant Israeli expertise and technologies.",
    pull: "The objective is to turn initial interest into concrete projects built around a real business need, the right partners and a workable path to implementation."
  },
  network: {
    heading: "Building the Moldova–Israel network",
    intro: "As Transition Bridge develops its first project pipeline, Polina is building relationships across several relevant professional circles.",
    center: "Moldova ↔ Israel",
    circles: [
      "Diplomatic & institutional relations",
      "Agriculture & viticulture",
      "Research & academia",
      "Energy & technology",
      "Finance & development institutions"
    ],
    note: "This work is still developing, and its purpose is practical: to create a network capable of helping a real project move from an initial business problem to the right expertise, partners and implementation pathway."
  },
  why: {
    heading: "Two countries, one working bridge",
    paragraphs: [
      "For Polina, Moldova and Israel are not simply two markets. They are the two countries that shaped her personal and professional life.",
      "Born in Moldova in 1977, Polina moved to Israel in 1992. Her personal history connects both countries, while her professional career was built in Israel across law, communications and mediation.",
      "Today, she is bringing these two parts of her background together through Transition Bridge, while rebuilding professional connections with Moldova. Her work is based on the idea that cooperation between the two countries can become much more practical when there are people who understand both environments and can help companies, experts and institutions find a common language."
    ],
    chronology: [
      ["1977", "Moldova", "Born in Moldova"],
      ["1992", "Israel", "Moved to Israel"],
      ["2026", "Moldova ↔ Israel", "Transition Bridge"]
    ],
    quote: "Good international cooperation starts with understanding what each side actually needs, and then finding the people who can turn that need into a workable project."
  },
  background: {
    heading: "Law, communication and mediation",
    intro: "Polina has two professional foundations that complement each other: communications and law.",
    qualifications: [
      ["Journalism & Public Relations", "Open University of Israel"],
      ["Law", "Netanya Academic College"],
      ["Licensed Attorney-at-Law", "Israel"],
      ["Certified Mediator", ""]
    ],
    practiceLabel: "Legal practice",
    practice: [
      "Contract law",
      "Agreements between companies and corporate entities",
      "Copyright and intellectual property",
      "Patent-related matters"
    ],
    mediationLabel: "Mediation",
    mediation: "Certified mediator with professional training in conflict resolution.",
    closing: "This combination of legal thinking, communication skills and mediation experience is particularly relevant to projects where several parties, jurisdictions and professional cultures need to work together."
  },
  languages: [
    ["Russian", "Native"],
    ["Hebrew", "Native"],
    ["English", "Professional working proficiency"],
    ["Romanian", "Basic · currently improving"]
  ],
  contact: {
    heading: "Discuss a Moldova–Israel project",
    text: "Interested in discussing a Moldova–Israel project, partnership or business challenge?",
    email: "polina@transitionbridge.org",
    whatsappLabel: "+972 54 814 4772",
    whatsappUrl: "https://wa.me/972548144772",
    vcard: "/polina-zisman.vcf"
  }
};

const sec = "py-[clamp(40px,5vw,56px)]";

function Eyebrow({ children }: { children: string }) {
  return <p className="eyebrow mb-4">{children}</p>;
}

function FounderProfile({ founder }: { founder: Founder }) {
  const { role, network, why, background, languages, contact, photo } = founder;

  return (
    <>
      {/* HERO */}
      <section className="bg-bg-primary pt-[calc(var(--header-height)+40px)] pb-[clamp(36px,5vw,56px)]">
        <div className="container-grid grid items-start gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
          <div className="max-w-[720px] lg:pt-10">
            <p className="eyebrow mb-5">ABOUT · FOUNDING TEAM</p>
            <h1 className="text-balance text-[clamp(2.2rem,5vw,3.6rem)] font-extrabold leading-[1.03]">{founder.name}</h1>
            <p className="mt-4 text-xl font-bold text-ink-primary">{founder.roleLine}</p>
            <p className="mt-2 text-sm font-semibold text-ink-secondary">{founder.credentialLine}</p>
            <p className="mt-6 max-w-[640px] text-lg leading-8 text-ink-secondary">{founder.intro}</p>
            {founder.contextLine && (
              <p className="mt-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.16em] text-ink-secondary">
                <span className="h-px w-8 bg-brand-blue/50" aria-hidden="true" />
                {founder.contextLine}
              </p>
            )}
          </div>
          <div className="w-full">
            <div className="rounded-3xl bg-[linear-gradient(135deg,rgba(36,87,245,0.28),rgba(18,175,203,0.2),rgba(40,201,139,0.22))] p-[2px]">
              <div className="overflow-hidden rounded-[22px] bg-surface">
                <img src={photo.src} alt={photo.alt} width={832} height={832} className="aspect-[4/5] w-full object-cover object-center" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROLE */}
      <section className={`bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <Eyebrow>HER ROLE AT TRANSITION BRIDGE</Eyebrow>
          <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:gap-16">
            <div>
              <h2 className="max-w-[520px] text-balance text-3xl font-bold leading-[1.06] md:text-[2.4rem]">{role.heading}</h2>
              <div className="mt-6 max-w-[640px] space-y-4 leading-8 text-ink-secondary">
                {role.paragraphs.map((p) => <p key={p}>{p}</p>)}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-ink-secondary">{role.focusLabel}</p>
              <ul className="mt-4 grid gap-2.5 text-sm leading-6 text-ink-secondary">
                {role.focus.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 border-b border-line pb-2.5 last:border-b-0 last:pb-0">
                    <span className="mt-2 block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan/70" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              {role.focusNote && <p className="mt-4 text-sm leading-6 text-ink-secondary">{role.focusNote}</p>}
            </div>
          </div>
          <p className="mt-10 max-w-[860px] border-l-4 border-brand-blue ps-6 text-xl font-semibold leading-relaxed text-ink-primary md:text-2xl">{role.pull}</p>
        </div>
      </section>

      {/* NETWORK */}
      {network && (
        <section className={`bg-bg-primary ${sec}`}>
          <div className="container-grid">
            <div className="max-w-[760px]">
              <Eyebrow>NETWORK DEVELOPMENT</Eyebrow>
              <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">{network.heading}</h2>
              <p className="mt-5 text-lg leading-8 text-ink-secondary">{network.intro}</p>
            </div>

            {/* Desktop hub */}
            <div className="mt-10 hidden lg:block">
              <div className="flex justify-center">
                <span className="rounded-full border border-brand-blue/30 bg-surface px-5 py-2.5 text-sm font-bold text-brand-blue">{network.center}</span>
              </div>
              <div className="mx-auto h-6 w-px bg-brand-cyan/40" aria-hidden="true" />
              <div className="relative">
                <div className="absolute left-0 right-0 top-0 h-px bg-[linear-gradient(90deg,rgba(36,87,245,0.35),rgba(18,175,203,0.35),rgba(40,201,139,0.35))]" aria-hidden="true" />
                <ul className="grid grid-cols-5 gap-4">
                  {network.circles.map((circle) => (
                    <li key={circle} className="relative pt-6">
                      <span className="absolute left-1/2 top-0 h-6 w-px -translate-x-1/2 bg-brand-cyan/40" aria-hidden="true" />
                      <div className="flex min-h-[96px] items-center justify-center rounded-xl border border-line bg-surface px-4 py-4 text-center text-sm font-semibold text-ink-secondary">{circle}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Mobile vertical flow */}
            <div className="relative mt-8 lg:hidden">
              <div className="absolute left-[7px] top-3 bottom-3 w-px bg-line" aria-hidden="true" />
              <ul className="grid gap-5">
                <li className="relative ps-8">
                  <span className="absolute start-0 top-1.5 h-3.5 w-3.5 rounded-full bg-brand-blue" aria-hidden="true" />
                  <span className="inline-flex rounded-full border border-brand-blue/30 bg-surface px-4 py-2 text-sm font-bold text-brand-blue">{network.center}</span>
                </li>
                {network.circles.map((circle) => (
                  <li key={circle} className="relative ps-8">
                    <span className="absolute start-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-brand-cyan bg-bg-primary" aria-hidden="true" />
                    <div className="rounded-xl border border-line bg-surface px-4 py-3 text-sm font-semibold text-ink-secondary">{circle}</div>
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-8 max-w-[820px] leading-8 text-ink-secondary">{network.note}</p>
          </div>
        </section>
      )}

      {/* WHY MOLDOVA × ISRAEL */}
      {why && (
        <section className={`petrol-section ${sec}`}>
          <div className="container-grid">
            <div className="max-w-[760px]">
              <p className="eyebrow-sage mb-4">WHY MOLDOVA × ISRAEL</p>
              <h2 className="text-balance text-3xl font-bold leading-[1.06] text-brand-darkText md:text-[2.6rem]">{why.heading}</h2>
            </div>
            <div className="relative mt-10 max-w-[640px]">
              <div className="absolute left-0 right-0 top-2 h-px bg-[linear-gradient(90deg,rgba(36,87,245,0.5),rgba(18,175,203,0.5),rgba(40,201,139,0.5))]" aria-hidden="true" />
              <ol className="grid grid-cols-3 gap-4">
                {why.chronology.map(([year, place, note]) => (
                  <li key={year} className="relative pt-8 text-center">
                    <span className="absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-brand-cyan bg-[#102a33]" aria-hidden="true" />
                    <p className="text-sm font-bold text-brand-cyan">{year}</p>
                    <p className="mt-0.5 font-semibold text-brand-darkText">{place}</p>
                    <p className="mt-1 text-sm leading-6 text-brand-darkMuted">{note}</p>
                  </li>
                ))}
              </ol>
            </div>
            <div className="mt-6 max-w-[760px] space-y-4 leading-8 text-brand-darkMuted">
              {why.paragraphs.map((p) => <p key={p}>{p}</p>)}
            </div>
            <blockquote className="mt-10 max-w-[860px] border-l-4 border-brand-cyan ps-6 text-2xl font-semibold leading-snug text-brand-darkText md:text-3xl">“{why.quote}”</blockquote>
          </div>
        </section>
      )}

      {/* PROFESSIONAL BACKGROUND */}
      <section className={`bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <Eyebrow>PROFESSIONAL BACKGROUND</Eyebrow>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">{background.heading}</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">{background.intro}</p>
          </div>
          <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-ink-secondary">Education & qualifications</p>
              <ul className="mt-5 grid gap-5">
                {background.qualifications.map(([title, institution]) => (
                  <li key={title} className="border-t border-line pt-4">
                    <p className="font-bold text-ink-primary">{title}</p>
                    {institution && <p className="mt-1 text-sm text-ink-secondary">{institution}</p>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-ink-secondary">{background.practiceLabel}</p>
              <ul className="mt-5 grid gap-2.5 text-sm leading-6 text-ink-secondary">
                {background.practice.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-2 block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan/70" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.16em] text-ink-secondary">{background.mediationLabel}</p>
              <p className="mt-4 leading-7 text-ink-secondary">{background.mediation}</p>
            </div>
          </div>
          <p className="mt-10 max-w-[860px] border-l-4 border-brand-blue ps-6 text-lg font-semibold leading-relaxed text-ink-primary">{background.closing}</p>
        </div>
      </section>

      {/* LANGUAGES */}
      <section className={`bg-bg-primary ${sec}`}>
        <div className="container-grid">
          <Eyebrow>LANGUAGES</Eyebrow>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {languages.map(([language, level]) => (
              <div key={language} className="border-t border-line pt-4">
                <p className="text-lg font-bold">{language}</p>
                <p className="mt-1 text-sm text-ink-secondary">{level}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONNECT */}
      <section className={`bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[720px]">
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.4rem]">{contact.heading}</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">{contact.text}</p>
            <div className="mt-7">
              <p className="text-lg font-bold">{founder.name}</p>
              <p className="text-sm text-ink-secondary">{founder.roleLine}</p>
            </div>
            <dl className="mt-6 grid gap-2 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <dt className="font-bold text-ink-secondary">Email:</dt>
                <dd><a className="text-brand-blue underline underline-offset-2" href={`mailto:${contact.email}`}>{contact.email}</a></dd>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <dt className="font-bold text-ink-secondary">WhatsApp:</dt>
                <dd><a className="text-brand-blue underline underline-offset-2" href={contact.whatsappUrl} target="_blank" rel="noopener noreferrer">{contact.whatsappLabel}</a></dd>
              </div>
            </dl>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button href={`mailto:${contact.email}`}>Email Polina</Button>
              <Button variant="secondary" href={contact.whatsappUrl} external>WhatsApp</Button>
              <Button variant="secondary" href={contact.vcard} download>Save contact</Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export function About() {
  return (
    <>
      <Seo title="About" description="Meet the founding team of Transition Bridge. Polina Zisman, co-founder, builds Moldova–Israel partnerships and cross-border project development." />
      <FounderProfile founder={polina} />
    </>
  );
}
